<template>
  <section class="dock-log-panel" :class="{ 'dock-log-panel--disabled': !canOperate }">
    <button type="button" class="dock-log-panel__header" :aria-expanded="expanded" @click="toggleExpanded">
      <span class="dock-log-panel__title">
        <strong><FileTextOutlined />设备日志</strong>
        <small>{{ summaryText }}</small>
      </span>
      <span class="dock-log-panel__chevron" :class="{ 'dock-log-panel__chevron--open': expanded }">
        <DownOutlined />
      </span>
    </button>

    <template v-if="expanded && canOperate">
      <div class="dock-log-panel__toolbar">
        <button
          type="button"
          class="dock-log-panel__link"
          :disabled="visibleKeys.length === 0"
          title="选中当前显示的全部日志"
          @click="toggleSelectAll"
        >
          {{ allSelected ? '取消全选' : '全选' }}
        </button>
        <span class="dock-log-panel__selection">{{ selectionText }}</span>
        <a-tooltip title="每 30 秒向机场重新拉取一次日志列表">
          <label class="dock-log-panel__auto">
            <a-switch v-model:checked="autoRefresh" size="small" aria-label="自动刷新设备日志列表" />
            <span>自动刷新</span>
          </label>
        </a-tooltip>
        <a-tooltip title="立即向机场拉取机场与飞行器的最新日志列表">
          <button type="button" class="dock-log-panel__icon-btn" :disabled="listLoading" @click="refreshEntries">
            <ReloadOutlined :spin="listLoading" />
          </button>
        </a-tooltip>
      </div>

      <div v-if="listLoading && !hasAnyEntry" class="dock-log-panel__empty">正在向机场读取日志列表…</div>
      <p v-else-if="listError" class="dock-log-panel__notice">
        <InfoCircleOutlined />{{ listError }}
      </p>

      <div v-for="group in groups" :key="group.module" class="dock-log-panel__group">
        <div class="dock-log-panel__group-title">
          <span class="dock-log-panel__group-name">
            <component :is="group.module === DRONE_LOG_MODULE ? RocketOutlined : HomeOutlined" />{{ group.label }}
          </span>
          <span class="dock-log-panel__group-count">{{ group.entries.length > 0 ? `${group.entries.length} 段` : '--' }}</span>
        </div>

        <p v-if="group.entries.length === 0" class="dock-log-panel__group-empty" :class="{ 'dock-log-panel__group-empty--error': group.result !== 0 }">
          {{ group.message }}<span v-if="group.result !== 0 && busyStreak > 0">（已自动重试 {{ busyStreak }} 次）</span>
        </p>

        <template v-else>
          <p v-if="group.stale" class="dock-log-panel__group-empty dock-log-panel__group-empty--error">
            {{ group.message }}{{ lastGoodText }}
          </p>
          <div class="dock-log-panel__list">
            <button
              v-for="entry in visibleEntriesOf(group)"
              :key="entry.boot_index"
              type="button"
              class="dock-log-panel__row"
              :class="{ 'dock-log-panel__row--selected': isSelected(group.module, entry.boot_index) }"
              :aria-pressed="isSelected(group.module, entry.boot_index)"
              @click="toggleEntry(group.module, entry.boot_index)"
            >
              <span class="dock-log-panel__check"><CheckOutlined v-if="isSelected(group.module, entry.boot_index)" /></span>
              <span class="dock-log-panel__row-time">{{ formatRange(entry) }}</span>
              <span class="dock-log-panel__row-size">{{ getLogSize(entry.size) }}</span>
            </button>
          </div>
          <button
            v-if="group.entries.length > DOCK_LOG_VISIBLE_LIMIT"
            type="button"
            class="dock-log-panel__more"
            @click="toggleShowAll(group.module)"
          >
            {{ showAll[group.module] ? `仅显示最新 ${DOCK_LOG_VISIBLE_LIMIT} 段` : `显示全部 ${group.entries.length} 段` }}
          </button>
        </template>
      </div>

      <div class="dock-log-panel__actions">
        <a-button
          size="small"
          type="primary"
          :disabled="selectedKeys.length === 0 || exporting"
          :loading="exporting"
          title="让机场把选中的日志打包上传，完成后可在下方记录里下载"
          @click="exportSelected"
        >
          <CloudUploadOutlined />导出所选日志
        </a-button>
        <span v-if="lastRefreshText" class="dock-log-panel__stamp">{{ lastRefreshText }}</span>
      </div>

      <div class="dock-log-panel__records">
        <div class="dock-log-panel__records-title">
          <span>导出记录</span>
          <button type="button" class="dock-log-panel__icon-btn" :disabled="recordsLoading" @click="refreshRecords">
            <ReloadOutlined :spin="recordsLoading" />
          </button>
        </div>
        <div v-if="records.length === 0" class="dock-log-panel__empty">暂无导出记录</div>
        <div v-for="record in records" :key="record.logs_id" class="dock-log-panel__record">
          <div class="dock-log-panel__record-main">
            <span class="dock-log-panel__record-time">{{ record.create_time || '--' }}</span>
            <span class="dock-log-panel__record-status">
              <i class="dock-log-panel__dot" :style="{ backgroundColor: statusColor(record.status) }"></i>
              {{ statusText(record.status) }}
            </span>
          </div>
          <div v-if="record.status === DockLogStatus.Uploading" class="dock-log-panel__progress">
            <span class="dock-log-panel__progress-bar"><i :style="{ width: percentOf(record) + '%' }"></i></span>
            <span class="dock-log-panel__progress-text">{{ percentOf(record) }}%</span>
          </div>
          <div class="dock-log-panel__record-actions">
            <a-button
              v-if="record.status === DockLogStatus.Uploading"
              size="small"
              danger
              :loading="cancellingId === record.logs_id"
              @click="cancelRecord(record)"
            >
              <StopOutlined />取消
            </a-button>
            <template v-else>
              <a-button
                v-for="file in downloadsOf(record)"
                :key="file.module"
                size="small"
                type="primary"
                :loading="downloadingId === `${record.logs_id}:${file.module}`"
                :title="`下载${file.label}日志压缩包`"
                @click="downloadRecord(record, file)"
              >
                <DownloadOutlined />{{ file.label }}
              </a-button>
              <a-button v-if="downloadsOf(record).length === 0" size="small" type="primary" disabled title="该记录没有可下载的日志包">
                <DownloadOutlined />下载
              </a-button>
              <a-button size="small" :loading="deletingId === record.logs_id" @click="deleteRecord(record)">
                <DeleteOutlined />删除
              </a-button>
            </template>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { computed, defineProps, onBeforeUnmount, ref, watch } from 'vue'
import { message, Modal } from 'ant-design-vue'
import {
  CheckOutlined,
  CloudUploadOutlined,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  FileTextOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  RocketOutlined,
  StopOutlined
} from '@ant-design/icons-vue'
import {
  cancelDeviceLogUpload,
  deleteDeviceLogUpload,
  getDeviceLogList,
  getDeviceUploadLogList,
  getUploadDeviceLogUrl,
  postDeviceUpgrade as postDockLogUpload,
  GetDeviceUploadLogListRsp,
  UploadDeviceLogBody
} from '/@/api/device-log'
import { DOMAIN } from '/@/types/device'
import { DeviceLogUploadStatusColor, DeviceLogUploadStatusMap } from '/@/types/device-log'
import { useDeviceLogUploadProgressEvent } from '/@/components/devices/device-log/use-device-log-upload-progress-event'
import {
  buildDockLogFileName,
  buildDockLogGroups,
  buildDockLogUploadBody,
  computeDockLogPercent,
  isDockLogBusyResponse,
  mergeDockLogModuleFiles,
  DOCK_LOG_VISIBLE_LIMIT,
  DRONE_LOG_MODULE,
  DockLogDownload,
  DockLogFileItem,
  DockLogGroup,
  DockLogModuleFiles,
  DockLogProgressPayload,
  DockLogRecord,
  DockLogStatus,
  DockLogUploadBody,
  dockLogEntryKey,
  findDockLogDownloads,
  isDockLogStatusEnd,
  isLargeDockLogExport,
  mergeDockLogProgress,
  summariseDockLogSelection
} from '/@/utils/dock-log'
import { bytesToSize } from '/@/utils/bytes'
import { download } from '/@/utils/download'
import { formatUnixTime } from '/@/utils/time'

// 定时器按 15 秒跳一次，下面几个常量决定隔几跳真正去拉一次列表
const LIST_TICK_INTERVAL = 15 * 1000
const TICKS_WHEN_IDLE = 2 // 机场空闲：30 秒
const TICKS_WHEN_BUSY_WITH_LIST = 8 // 机场忙但手上有列表：2 分钟，别一直敲它
const TICKS_WHEN_BUSY_EMPTY = 1 // 机场忙且什么都没有：15 秒，用户正对着空面板等
const RECORD_POLL_INTERVAL = 8 * 1000
const RECORD_PAGE_SIZE = 5

const props = defineProps<{
  sn: string,
}>()

const expanded = ref(false)
// 每个模块各自记「是否展开全部」
const showAll = ref<Record<string, boolean>>({})
const autoRefresh = ref(true)
const listLoading = ref(false)
const listError = ref('')
const lastRefreshAt = ref(0)
// 上一次真正读到列表的时间，以及连续被机场挡回来的次数（用来退避自动刷新）
const lastGoodAt = ref(0)
const busyStreak = ref(0)
const moduleFileList = ref<DockLogModuleFiles[]>([])
// 选中项用 `${module}:${boot_index}`：boot_index 在机场和飞行器之间会重号
const selectedKeys = ref<string[]>([])

const records = ref<DockLogRecord[]>([])
const recordsLoading = ref(false)
const exporting = ref(false)
const trackedLogsId = ref('')
const downloadingId = ref('')
const cancellingId = ref('')
const deletingId = ref('')

// 查看和导出日志不依赖远程调试：后端只要求设备在线，机场侧也没有这个前置条件
const canOperate = computed(() => !!props.sn)
// 机场和飞行器各一组，飞行器没开机时也留个空壳说明原因
const groups = computed(() => buildDockLogGroups(moduleFileList.value))
const hasAnyEntry = computed(() => groups.value.some(group => group.entries.length > 0))
const visibleKeys = computed(() => groups.value.flatMap(group =>
  visibleEntriesOf(group).map(entry => dockLogEntryKey(group.module, entry.boot_index))))
const selection = computed(() => summariseDockLogSelection(groups.value, selectedKeys.value))
const allSelected = computed(() => visibleKeys.value.length > 0 &&
  visibleKeys.value.every(key => selectedKeys.value.includes(key)))

const selectionText = computed(() => {
  const { count, size } = selection.value
  if (count > 0) {
    return `已选 ${count} 段 · ${bytesToSize(size)}`
  }
  const total = groups.value.reduce((sum, group) => sum + group.entries.length, 0)
  return `共 ${total} 段日志`
})

const summaryText = computed(() => {
  const uploading = records.value.filter(record => record.status === DockLogStatus.Uploading).length
  if (uploading > 0) {
    return `${uploading} 个导出任务进行中`
  }
  const parts = groups.value.filter(group => group.entries.length > 0)
    .map(group => `${group.label} ${group.entries.length} 段`)
  return parts.length > 0 ? parts.join(' · ') : '实时查看并导出机场与飞行器日志'
})

const lastRefreshText = computed(() => (lastRefreshAt.value ? `更新于 ${formatClock(lastRefreshAt.value)}` : ''))
const lastGoodText = computed(() => (lastGoodAt.value ? `，以下为 ${formatClock(lastGoodAt.value)} 读到的列表` : ''))

function getLogSize (size: number) {
  return bytesToSize(size)
}

function formatClock (stamp: number) {
  const date = new Date(stamp)
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
}

// 面板只有 390px 宽，同一天的日志段省掉重复的日期
function formatRange (entry: DockLogFileItem) {
  const start = formatUnixTime(entry.start_time, 'MM-DD HH:mm')
  const sameDay = formatUnixTime(entry.start_time, 'YYYY-MM-DD') === formatUnixTime(entry.end_time, 'YYYY-MM-DD')
  const end = formatUnixTime(entry.end_time, sameDay ? 'HH:mm' : 'MM-DD HH:mm')
  return `${start} ~ ${end}`
}

function statusText (status: number) {
  return DeviceLogUploadStatusMap[status as keyof typeof DeviceLogUploadStatusMap] || '--'
}

function statusColor (status: number) {
  return DeviceLogUploadStatusColor[status as keyof typeof DeviceLogUploadStatusColor] || '#8fa0ac'
}

function percentOf (record: DockLogRecord) {
  return computeDockLogPercent(record)
}

function downloadsOf (record: DockLogRecord) {
  return findDockLogDownloads(record)
}

// 机场可能一口气报几百段，默认每个模块只铺最新的一批
function visibleEntriesOf (group: DockLogGroup) {
  return showAll.value[group.module] ? group.entries : group.entries.slice(0, DOCK_LOG_VISIBLE_LIMIT)
}

function toggleShowAll (module: string) {
  showAll.value = { ...showAll.value, [module]: !showAll.value[module] }
}

function isSelected (module: string, bootIndex: number) {
  return selectedKeys.value.includes(dockLogEntryKey(module, bootIndex))
}

function toggleExpanded () {
  expanded.value = !expanded.value
}

function toggleEntry (module: string, bootIndex: number) {
  const key = dockLogEntryKey(module, bootIndex)
  selectedKeys.value = selectedKeys.value.includes(key)
    ? selectedKeys.value.filter(item => item !== key)
    : selectedKeys.value.concat(key)
}

// 全选只作用于当前铺出来的行，避免一键勾上几百段、几百 GB
function toggleSelectAll () {
  const visible = visibleKeys.value
  selectedKeys.value = allSelected.value
    ? selectedKeys.value.filter(key => !visible.includes(key))
    : Array.from(new Set(selectedKeys.value.concat(visible)))
}

// 实时日志列表：每次调用都会让后端向机场发一次 fileupload_list 请求
async function refreshEntries () {
  if (!canOperate.value || listLoading.value) {
    return
  }
  listLoading.value = true
  try {
    const { code, data } = await getDeviceLogList({
      device_sn: props.sn,
      domain: [DOMAIN.DOCK, DOMAIN.DRONE]
    })
    if (code === 0) {
      const files = data?.files || []
      // 机场忙的时候只回错误码，保留上一次的列表，别把面板清空
      const busy = isDockLogBusyResponse(files)
      moduleFileList.value = mergeDockLogModuleFiles(moduleFileList.value, files)
      // 机场重启后 boot_index 会变，丢掉已经不在列表里的选中项
      const valid = new Set(buildDockLogGroups(moduleFileList.value).flatMap(group =>
        group.entries.map(entry => dockLogEntryKey(group.module, entry.boot_index))))
      selectedKeys.value = selectedKeys.value.filter(key => valid.has(key))
      listError.value = ''
      lastRefreshAt.value = Date.now()
      busyStreak.value = busy ? busyStreak.value + 1 : 0
      if (!busy) {
        lastGoodAt.value = Date.now()
      }
    } else {
      listError.value = '机场未返回日志列表'
    }
  } catch (e) {
    listError.value = '读取设备日志列表失败，机场可能离线'
  } finally {
    listLoading.value = false
  }
}

async function refreshRecords () {
  if (!props.sn || recordsLoading.value) {
    return
  }
  recordsLoading.value = true
  try {
    const { code, data } = await getDeviceUploadLogList({
      device_sn: props.sn,
      page: 1,
      page_size: RECORD_PAGE_SIZE
    })
    if (code === 0) {
      const list: GetDeviceUploadLogListRsp[] = data?.list || []
      records.value = list as DockLogRecord[]
    }
  } catch (e) {
    // 列表读取失败时保留上一次结果，拦截器已经提示过了
  } finally {
    recordsLoading.value = false
  }
}

function exportSelected () {
  if (!canOperate.value || exporting.value) {
    return
  }
  const body = buildDockLogUploadBody({
    deviceSn: props.sn,
    groups: groups.value,
    selectedKeys: selectedKeys.value,
    information: `远程调试导出设备日志 ${props.sn}`
  })
  if (!body) {
    message.warning('请先选择要导出的日志')
    return
  }
  const { count, size } = selection.value
  // 单段日志就有几百 MB，打包上传期间机场上行带宽会被占满，体积大了先问一句
  if (isLargeDockLogExport(size)) {
    Modal.confirm({
      title: '导出较大的日志',
      content: `本次将让机场打包上传 ${count} 段日志，约 ${bytesToSize(size)}，期间会占用机场上行带宽。确认继续吗？`,
      onOk: () => runExport(body)
    })
    return
  }
  runExport(body)
}

async function runExport (body: DockLogUploadBody) {
  exporting.value = true
  try {
    const { code } = await postDockLogUpload(body as UploadDeviceLogBody)
    if (code === 0) {
      message.success('导出任务已下发，机场正在打包上传')
      selectedKeys.value = []
      await refreshRecords()
      // 下发接口不回 logs_id，最新的那条上传中记录就是刚下发的任务
      const pending = records.value.find(record => record.status === DockLogStatus.Uploading)
      trackedLogsId.value = pending?.logs_id || ''
    }
  } catch (e) {
    message.error('导出任务下发失败')
  } finally {
    exporting.value = false
  }
}

async function downloadRecord (record: DockLogRecord, file: DockLogDownload) {
  if (!file || downloadingId.value) {
    return
  }
  downloadingId.value = `${record.logs_id}:${file.module}`
  try {
    const { code, data } = await getUploadDeviceLogUrl({
      logs_id: record.logs_id,
      file_id: file.fileId
    })
    if (code === 0 && data) {
      download(data, buildDockLogFileName(props.sn, record.create_time, file.module))
    } else {
      message.error(`获取${file.label}日志下载地址失败`)
    }
  } catch (e) {
    message.error(`获取${file.label}日志下载地址失败`)
  } finally {
    downloadingId.value = ''
  }
}

function cancelRecord (record: DockLogRecord) {
  Modal.confirm({
    title: '取消日志导出',
    content: '确认让机场停止本次日志上传吗？',
    okType: 'danger',
    onOk: () => cancelRecordOk(record)
  })
}

async function cancelRecordOk (record: DockLogRecord) {
  cancellingId.value = record.logs_id
  try {
    const { code } = await cancelDeviceLogUpload({
      device_sn: props.sn,
      module_list: [DOMAIN.DOCK, DOMAIN.DRONE],
      status: 'cancel'
    })
    if (code === 0) {
      if (trackedLogsId.value === record.logs_id) {
        trackedLogsId.value = ''
      }
      await refreshRecords()
    }
  } catch (e) {
    message.error('取消导出失败')
  } finally {
    cancellingId.value = ''
  }
}

function deleteRecord (record: DockLogRecord) {
  Modal.confirm({
    title: '删除导出记录',
    content: '删除后将无法再下载该日志包，确认删除吗？',
    okType: 'danger',
    onOk: () => deleteRecordOk(record)
  })
}

async function deleteRecordOk (record: DockLogRecord) {
  deletingId.value = record.logs_id
  try {
    const { code } = await deleteDeviceLogUpload({
      device_sn: props.sn,
      logs_id: record.logs_id
    })
    if (code === 0) {
      await refreshRecords()
    }
  } catch (e) {
    message.error('删除记录失败')
  } finally {
    deletingId.value = ''
  }
}

// fileupload_progress 推送：先就地更新进度，进到终态再拉一次记录换 file_id
function onUploadProgress (payload: DockLogProgressPayload) {
  if (!payload || (payload.sn && props.sn && payload.sn !== props.sn)) {
    return
  }
  const record = mergeDockLogProgress(records.value, payload)
  if (!record || !isDockLogStatusEnd(record.status)) {
    return
  }
  const tracked = trackedLogsId.value === record.logs_id
  if (tracked) {
    trackedLogsId.value = ''
    if (record.status === DockLogStatus.Done) {
      message.success('设备日志导出完成，可在导出记录中下载')
    } else if (record.status === DockLogStatus.Failed) {
      message.error('设备日志导出失败')
    }
  }
  refreshRecords()
}

useDeviceLogUploadProgressEvent(onUploadProgress)

/**
 * 机场在远程调试（开关机、开合舱盖）、打包上传日志、飞行任务期间都会挡掉 fileupload_list。
 * 手上已经有列表时慢慢等；一条都没有时催得紧一点，好让面板尽快自己长出内容。
 */
function refreshEveryTicks () {
  if (busyStreak.value === 0) {
    return TICKS_WHEN_IDLE
  }
  return hasAnyEntry.value ? TICKS_WHEN_BUSY_WITH_LIST : TICKS_WHEN_BUSY_EMPTY
}

let listTimer = 0
let recordTimer = 0

function stopTimers () {
  if (listTimer) {
    window.clearInterval(listTimer)
    listTimer = 0
  }
  if (recordTimer) {
    window.clearInterval(recordTimer)
    recordTimer = 0
  }
}

function startTimers () {
  stopTimers()
  let tick = 0
  listTimer = window.setInterval(() => {
    tick += 1
    if (!autoRefresh.value || document.visibilityState !== 'visible') {
      return
    }
    if (tick % refreshEveryTicks() === 0) {
      refreshEntries()
    }
  }, LIST_TICK_INTERVAL)
  // ws 断线时兜底：只在有任务进行中时轮询记录
  recordTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible' && records.value.some(record => record.status === DockLogStatus.Uploading)) {
      refreshRecords()
    }
  }, RECORD_POLL_INTERVAL)
}

function resetState () {
  stopTimers()
  moduleFileList.value = []
  selectedKeys.value = []
  showAll.value = {}
  records.value = []
  listError.value = ''
  lastRefreshAt.value = 0
  lastGoodAt.value = 0
  busyStreak.value = 0
  trackedLogsId.value = ''
}

watch(() => props.sn, () => {
  resetState()
  if (expanded.value && canOperate.value) {
    refreshEntries()
    refreshRecords()
    startTimers()
  }
})

watch([canOperate, expanded], ([operable, open]) => {
  if (!operable || !open) {
    stopTimers()
    return
  }
  refreshEntries()
  refreshRecords()
  startTimers()
})

onBeforeUnmount(stopTimers)
</script>

<style lang="scss" scoped>
.dock-log-panel {
  padding: 8px 10px 10px;
  color: #f4f7fa;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.dock-log-panel--disabled {
  background: rgba(255, 255, 255, 0.02);
}

.dock-log-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 0;
  color: inherit;
  text-align: left;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.dock-log-panel__title {
  min-width: 0;

  strong,
  small {
    display: block;
  }

  strong {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 650;
  }

  strong :deep(.anticon) {
    color: #7cc4ff;
    font-size: 15px;
  }

  small {
    margin-top: 1px;
    overflow: hidden;
    color: #8fa0ac;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.dock-log-panel__chevron {
  flex: 0 0 auto;
  color: #b8bec5;
  font-size: 11px;
  transition: transform 0.15s ease;

  &--open {
    transform: rotate(180deg);
  }
}

.dock-log-panel__notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 0 0;
  padding: 5px 8px;
  color: #e0b064;
  font-size: 10px;
  line-height: 1.5;
  background: rgba(224, 176, 100, 0.1);
  border: 1px solid rgba(224, 176, 100, 0.28);
  border-radius: 7px;
}

.dock-log-panel__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.dock-log-panel__link {
  flex: 0 0 auto;
  padding: 0;
  color: #7cc4ff;
  font-size: 10px;
  background: transparent;
  border: 0;
  cursor: pointer;

  &[disabled] {
    color: #6c757d;
    cursor: not-allowed;
  }
}

.dock-log-panel__selection {
  flex: 1 1 auto;
  overflow: hidden;
  color: #8fa0ac;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dock-log-panel__auto {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  margin: 0;
  color: #8fa0ac;
  font-size: 10px;
  cursor: pointer;
}

.dock-log-panel__icon-btn {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  color: #d2d8de;
  font-size: 12px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 4px;
  cursor: pointer;

  &:hover:not([disabled]) {
    color: #fff;
    border-color: rgba(124, 196, 255, 0.6);
  }

  &[disabled] {
    color: #6c757d;
    cursor: not-allowed;
  }
}

.dock-log-panel__group {
  margin-top: 8px;
}

.dock-log-panel__group-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: #b8bec5;
  font-size: 11px;
  font-weight: 600;
}

.dock-log-panel__group-name {
  display: inline-flex;
  align-items: center;
  gap: 5px;

  :deep(.anticon) {
    color: #7cc4ff;
    font-size: 12px;
  }
}

.dock-log-panel__group-count {
  color: #8fa0ac;
  font-size: 10px;
  font-weight: 400;
}

.dock-log-panel__group-empty {
  margin: 4px 0 0;
  padding: 5px 8px;
  color: #8fa0ac;
  font-size: 10px;
  line-height: 1.5;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 7px;

  &--error {
    color: #e0b064;
    background: rgba(224, 176, 100, 0.1);
    border-color: rgba(224, 176, 100, 0.28);
  }
}

.dock-log-panel__list {
  margin-top: 4px;
  max-height: 148px;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 7px;
}

.dock-log-panel__empty {
  padding: 12px 8px;
  color: #8fa0ac;
  font-size: 10px;
  text-align: center;
}

.dock-log-panel__row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 5px 8px;
  color: #dfe5ea;
  font-size: 11px;
  text-align: left;
  background: transparent;
  border: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  &--selected {
    color: #fff;
    background: rgba(30, 142, 245, 0.16);
  }
}

.dock-log-panel__check {
  display: inline-flex;
  flex: 0 0 14px;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  color: #fff;
  font-size: 9px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 3px;

  .dock-log-panel__row--selected & {
    background: #168bff;
    border-color: #168bff;
  }
}

.dock-log-panel__row-time {
  flex: 1 1 auto;
  overflow: hidden;
  font-family: Menlo, Monaco, Consolas, 'Courier New', monospace;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dock-log-panel__row-size {
  flex: 0 0 auto;
  color: #a9b2ba;
  font-size: 10px;
}

.dock-log-panel__more {
  width: 100%;
  margin-top: 4px;
  padding: 4px 8px;
  color: #7cc4ff;
  font-size: 10px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
  }
}

.dock-log-panel__actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.dock-log-panel__stamp {
  overflow: hidden;
  color: #8fa0ac;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dock-log-panel__records {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.dock-log-panel__records-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #b8bec5;
  font-size: 11px;
  font-weight: 600;
}

.dock-log-panel__record {
  margin-top: 6px;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 7px;
}

.dock-log-panel__record-main {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.dock-log-panel__record-time {
  overflow: hidden;
  color: #dfe5ea;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dock-log-panel__record-status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 4px;
  color: #b8bec5;
  font-size: 10px;
}

.dock-log-panel__dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.dock-log-panel__progress {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
}

.dock-log-panel__progress-bar {
  flex: 1 1 auto;
  height: 4px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 2px;

  i {
    display: block;
    height: 100%;
    background: #168bff;
    transition: width 0.2s ease;
  }
}

.dock-log-panel__progress-text {
  flex: 0 0 auto;
  color: #8fa0ac;
  font-size: 10px;
}

.dock-log-panel__record-actions {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

:deep(.ant-switch-small) {
  min-width: 26px;
  height: 14px;
  line-height: 14px;
}

:deep(.ant-switch-small .ant-switch-handle) {
  top: 1px;
  width: 12px;
  height: 12px;
}
</style>
